// src/components/common/VideoListComponent.vue
<script setup lang="ts">
import BaseVideoThumbnail from '@/components/base/BaseVideoThumbnail.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { Lecture } from '@/types/lecture.types'
import { computed, defineEmits, defineProps, ref } from 'vue'

const props = defineProps<{
  videos: Lecture[]
  gridColumns?: number // 그리드 열 개수 (기본값: 3)
  className?: string // 추가 클래스 이름
}>()

const emit = defineEmits<{
  (e: 'videoClick', videoId: number): void
}>()

const isMobile = useIsMobile()

const hoveredVideoId = ref<number | null>(null)

const handleMouseEnter = (videoId: number) => {
  if (isMobile.value) return
  hoveredVideoId.value = videoId
}

const handleMouseLeave = () => {
  if (isMobile.value) return
  hoveredVideoId.value = null
}

const handleVideoClick = (videoId: number) => {
  emit('videoClick', videoId)
}

const gridColumnsStyle = computed(() => {
  return props.gridColumns || 3
})
</script>

<template>
  <div
    class="video-list-component"
    :class="props.className"
    :style="{
      'grid-template-columns': `repeat(${gridColumnsStyle}, 1fr)`,
    }"
  >
    <BaseVideoThumbnail
      class="video-list-item"
      v-for="video in props.videos"
      :key="video.idx"
      :lecture="video"
      :show-level="isMobile || hoveredVideoId === video.idx"
      :is-free="video.free"
      @mouseenter="handleMouseEnter(video.idx)"
      @mouseleave="handleMouseLeave"
      @videoClick="handleVideoClick(video.idx)"
    />

    <!-- 결과가 없는 경우 -->
    <div v-if="props.videos.length === 0" class="empty-state">
      <p class="body-md gray-600">검색 결과가 없습니다.</p>
    </div>
  </div>
</template>

<style scoped>
.video-list-component {
  display: grid;
  gap: 20px;
  width: 100%;
}

.video-list-item {
  transition: transform 0.2s ease;
}

.video-list-item:hover {
  transform: scale(1.24);
  z-index: 100;
  border: 1px solid var(--tint-300);
}

.video-list-component:has(.video-list-item:hover) .video-list-item:not(:hover) {
  opacity: 0.2;
  transition: opacity 0.1s ease;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
}

@media (max-width: 1365px) {
  .video-list-component {
    grid-template-columns: 1fr !important;
    gap: 16px;
  }

  .video-list-item:hover {
    transform: none;
    border: none;
  }

  .video-list-component:has(.video-list-item:hover) .video-list-item:not(:hover) {
    opacity: 1;
    transition: none;
  }
}
</style>
